/**
 * Client-side auth utilities.
 *
 * Strategy (matches server/src/auth.ts):
 *   - Token = HS256 JWT issued by POST /auth on the game server.
 *   - Stored in localStorage so reloads + duplicate tabs reuse the token
 *     until expiry; clearing it logs the user out.
 *   - We never re-sign or verify client-side — the server is the source of
 *     truth. We only parse `exp` to skip the password prompt when a valid
 *     token is still in flight.
 *
 * Network failures are surfaced to the caller (the prompt UI) so the user
 * sees "wrong password" or "server unreachable" instead of a silent hang.
 */

const STORAGE_KEY = "vibe-auth-token";

/** Read the cached bearer token. Returns null if nothing stored. */
export function getStoredToken(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    // jsdom-less environments, private mode, etc.
    return null;
  }
}

/** Cache the bearer token for future reloads. */
export function setStoredToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* private mode → no-op; modal will just re-prompt next time */
  }
}

/** Drop the cached token (logout). */
export function clearStoredToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
}

/**
 * Returns true if the token is missing OR its `exp` claim is in the past
 * (or within `safetyMarginMs` of expiry, to avoid edge-case races on slow
 * networks). NOT a cryptographic check — server re-verifies on every request.
 */
export function isTokenExpired(token: string | null, safetyMarginMs = 30_000): boolean {
  if (!token) return true;
  const payload = decodePayloadUnsafe(token);
  if (!payload) return true;
  const expMs = payload.exp * 1000;
  return expMs - Date.now() < safetyMarginMs;
}

/** Returned subset of the JWT payload — only the claims we use client-side. */
export interface ClientTokenPayload {
  sub: string;
  iat: number;
  exp: number;
}

/**
 * Stateless decode of the JWT payload — does NOT verify the signature. Only
 * use for non-security display purposes (e.g. "session expires at X"). Returns
 * null on malformed input.
 */
export function decodePayloadUnsafe(token: string): ClientTokenPayload | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const decoded = base64UrlDecode(parts[1]!);
    const parsed = JSON.parse(decoded) as Partial<ClientTokenPayload>;
    if (
      typeof parsed.sub !== "string" ||
      typeof parsed.iat !== "number" ||
      typeof parsed.exp !== "number"
    ) {
      return null;
    }
    return { sub: parsed.sub, iat: parsed.iat, exp: parsed.exp };
  } catch {
    return null;
  }
}

function base64UrlDecode(s: string): string {
  // Replace url-safe chars with standard base64 + pad.
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4 !== 0) b64 += "=";
  // atob is in DOM lib; client builds always target the browser.
  if (typeof atob === "function") {
    const bin = atob(b64);
    let out = "";
    for (let i = 0; i < bin.length; i++) out += String.fromCharCode(bin.charCodeAt(i));
    return decodeURIComponent(escape(out));
  }
  // Node fallback for tests run under vitest's jsdom-less environment.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buf = (globalThis as any).Buffer?.from(b64, "base64");
  if (buf) return buf.toString("utf8");
  throw new Error("no base64 decoder available");
}

export interface ExchangeResult {
  token: string;
  expiresAt: number;
  sub?: string;
}

/**
 * POST `{authUrl}/auth` with the password. Returns the token + expiresAt
 * (ms-epoch). Throws on network errors or non-200 responses; the prompt UI
 * inspects the thrown Error's message to distinguish "wrong password" (401)
 * from "server down" (network fail).
 */
export async function exchangePassword(authUrl: string, password: string): Promise<ExchangeResult> {
  const base = authUrl.replace(/\/+$/, "");
  const res = await fetch(`${base}/auth`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (res.status === 401) throw new AuthError("incorrect password", 401);
  if (res.status === 503) throw new AuthError("server has no password configured (503)", 503);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new AuthError(
      `auth failed (status=${res.status}): ${body.slice(0, 120)}`,
      res.status,
    );
  }
  const payload = (await res.json()) as Partial<ExchangeResult>;
  if (typeof payload.token !== "string" || typeof payload.expiresAt !== "number") {
    throw new AuthError("auth returned malformed body", 500);
  }
  const out: ExchangeResult = { token: payload.token, expiresAt: payload.expiresAt };
  if (typeof payload.sub === "string") out.sub = payload.sub;
  return out;
}

/** Typed error so the modal can distinguish "wrong password" from "network down". */
export class AuthError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = "AuthError";
  }
}
